import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { reassignDeviceMutation } from '../../lib/rescueQueries';
import { useAssignDevice } from '../../hooks/useAssignDevice';
import { usePreAssignDeviceCheck } from '../../hooks/usePreAssignDeviceCheck';
import styles from './AssignDeviceToPetModal.module.scss';

interface PetInfo {
  id: string;
  name: string;
  breed?: { name: string } | null;
  chip?: { serialNumber: string } | null;
}

interface AssignDeviceToPetModalProps {
  open: boolean;
  onClose: () => void;
  pet: PetInfo | null;
}

type AssignMode = 'serial' | 'stock';

export default function AssignDeviceToPetModal({ open, onClose, pet }: AssignDeviceToPetModalProps) {
  const { showToast } = useToast();
  const [mode, setMode] = useState<AssignMode>('serial');
  const [serialNumber, setSerialNumber] = useState('');
  const [serialError, setSerialError] = useState('');
  const [reassignTarget, setReassignTarget] = useState<{ moduleId: string; petName: string } | null>(null);
  // The serial the user was told isn't on their books yet; assigning it adds it to them.
  const [addTarget, setAddTarget] = useState<string | null>(null);

  const { assign, assigning } = useAssignDevice();
  const [reassignDevice, { loading: reassigning }] = useMutation(reassignDeviceMutation, {
    refetchQueries: 'active' as const,
  });
  const { check: preAssignCheck, checking } = usePreAssignDeviceCheck();

  const busy = assigning || reassigning || checking;

  function handleClose() {
    setMode('serial');
    setSerialNumber('');
    setSerialError('');
    setReassignTarget(null);
    setAddTarget(null);
    onClose();
  }

  async function handleAssign() {
    // Re-entrancy guard: the serial input's Enter handler fires regardless of
    // disabled state, so a habitual double-tap would otherwise confirm a
    // pending reassign before the user has read the notice.
    if (busy) {
      return;
    }
    if (!pet) {
      return;
    }
    const moduleId = serialNumber.trim().toUpperCase();
    if (!moduleId) {
      setSerialError('Serial number is required');
      return;
    }
    // Second click after a reassignment notice — the user has acknowledged that
    // the device will be moved off its current pet, so skip the pre-checks.
    const confirmedReassign = reassignTarget?.moduleId === moduleId;
    const confirmedAdd = addTarget === moduleId;
    setSerialError('');

    try {
      if (!confirmedReassign && !confirmedAdd) {
        const verdict = await preAssignCheck({ moduleId, currentPetId: pet.id });
        if (verdict.type === 'blocked') {
          setSerialError(verdict.message);
          return;
        }
        if (verdict.type === 'reassign') {
          setReassignTarget({ moduleId, petName: verdict.petName });
          return;
        }
        if (verdict.type === 'addToInventory') {
          setAddTarget(moduleId);
          return;
        }
      }

      if (confirmedReassign) {
        await reassignDevice({ variables: { moduleId, toPetId: pet.id } });
      } else {
        await assign({ moduleId, petId: pet.id, addToInventory: confirmedAdd });
      }
      showToast(`Device ${moduleId} assigned to ${pet.name}`);
      handleClose();
    } catch (err) {
      // The confirmation no longer holds, so the next submit runs the checks again.
      setReassignTarget(null);
      setAddTarget(null);
      setSerialError(err instanceof Error ? err.message : 'Failed to assign device');
    }
  }

  if (!pet) {
    return null;
  }

  const subtitle = [pet.breed?.name, pet.chip ? `Chip ${pet.chip.serialNumber}` : null].filter(Boolean).join(' · ');
  const canSubmit = serialNumber.trim().length > 0 && !busy;
  const moduleIdNormalized = serialNumber.trim().toUpperCase();
  const reassignActive = !!reassignTarget && reassignTarget.moduleId === moduleIdNormalized;
  const addActive = addTarget === moduleIdNormalized;

  return (
    <Modal open={open} onClose={handleClose} size="wide">
      <ModalTitle>Add Device to {pet.name}</ModalTitle>
      {subtitle && <p className={styles.subtitle}>{subtitle}</p>}

      <div className={styles.modeLabel}>Two ways to add a device &mdash; enter serial or pick from stock</div>

      <div className={styles.radioCards}>
        <button
          type="button"
          className={`${styles.radioCard} ${mode === 'serial' ? styles.radioCardSelected : ''}`}
          onClick={() => setMode('serial')}
        >
          <span className={`${styles.radioDot} ${mode === 'serial' ? styles.radioDotSelected : ''}`} />
          <div>
            <div className={styles.radioLabel}>Enter device serial number</div>
            <div className={styles.radioDesc}>Enter a serial number to assign a new device</div>
          </div>
        </button>
        <button
          type="button"
          className={`${styles.radioCard} ${mode === 'stock' ? styles.radioCardSelected : ''}`}
          onClick={() => setMode('stock')}
          disabled
          style={{ opacity: 0.5, cursor: 'not-allowed' }}
        >
          <span className={`${styles.radioDot} ${mode === 'stock' ? styles.radioDotSelected : ''}`} />
          <div>
            <div className={styles.radioLabel}>Select from unassigned devices</div>
            <div className={styles.radioDesc}>Coming soon</div>
          </div>
        </button>
      </div>

      <PortalField label="Serial Number" error={serialError} showError={!!serialError}>
        <PortalInput
          value={serialNumber}
          onChange={(e) => {
            setSerialNumber(e.target.value);
            setSerialError('');
            setReassignTarget(null);
            setAddTarget(null);
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleAssign()}
          placeholder="e.g. M1XXXXXXXX"
          hasError={!!serialError}
          autoFocus
        />
      </PortalField>

      {reassignActive ? (
        <div className={styles.reassignNotice}>
          Device <strong>{reassignTarget!.moduleId}</strong> is currently attached to{' '}
          <strong>{reassignTarget!.petName}</strong>. Continuing will remove it from{' '}
          <strong>{reassignTarget!.petName}</strong> and assign it to <strong>{pet.name}</strong>.
        </div>
      ) : addActive ? (
        <div className={styles.reassignNotice}>
          Device <strong>{moduleIdNormalized}</strong> isn&rsquo;t in your inventory yet. Assigning it to{' '}
          <strong>{pet.name}</strong> also adds it to your device inventory.
        </div>
      ) : (
        <div className={styles.infoBanner}>
          If this device hasn&rsquo;t been activated yet, plug it in via USB-C after assigning. Battery and location
          data will appear once the device powers on.
        </div>
      )}

      <ModalActions>
        <Button variant="ghost" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleAssign} disabled={!canSubmit}>
          {checking
            ? 'Checking device...'
            : reassigning
              ? 'Reassigning...'
              : assigning
                ? 'Assigning...'
                : reassignActive
                  ? `Reassign to ${pet.name}`
                  : addActive
                    ? `Add and assign to ${pet.name}`
                    : `Assign to ${pet.name}`}
        </Button>
      </ModalActions>
    </Modal>
  );
}
